import axios, { type InternalAxiosRequestConfig } from 'axios'

import { env } from '@/config'
import { useUserStore } from '@/store'

export const apiClient = axios.create({
  baseURL: env.API_BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
})

export const authClient = axios.create({
  baseURL: env.API_BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
})

type AccessTokenRefresher = () => Promise<string>

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _authRetried?: boolean
}

let refreshAccessToken: AccessTokenRefresher | null = null

export function configureAccessTokenRefresh(handler: AccessTokenRefresher): void {
  refreshAccessToken = handler
}

apiClient.interceptors.request.use((config) => {
  const accessToken = useUserStore.getState().accessToken
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      return Promise.reject(error)
    }

    const config = error.config as RetryableRequestConfig | undefined
    if (!config || config._authRetried || !refreshAccessToken) {
      return Promise.reject(error)
    }

    config._authRetried = true
    try {
      const accessToken = await refreshAccessToken()
      config.headers.Authorization = `Bearer ${accessToken}`
      return apiClient(config)
    } catch {
      useUserStore.getState().clearSession()
      return Promise.reject(error)
    }
  },
)

export function getApiErrorMessage(
  error: unknown,
  fallback?: string
) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data

    if (typeof data === 'string' && data.trim()) {
      return data
    }

    if (data && typeof data === 'object') {
      const message = 'message' in data
        ? data.message
        : 'detail' in data
          ? data.detail
          : 'reason' in data
            ? data.reason
            : 'error' in data
              ? data.error
              : null

      if (typeof message === 'string' && message.trim()) {
        return message
      }
    }
  }

  return error instanceof Error ? error.message : (fallback ?? 'Ocurrió un error inesperado')
}
